import { AvailableLanguages } from '@repo/toolkit/language-types'
import { TFunction } from 'i18next'
import { z, ZodType } from 'zod'

import { AffectedStaffStatusEnum } from '../../../lib/types/enums/affected-staff/affected-staff-status.enum'
import { TravelChoiceEnum } from '../../../lib/types/enums/affected-staff/travel-choice.enum'
import { CurrencyEnum } from '../../../lib/types/enums/common/currency.enum'
import { AffectedStaffInput } from '../../../lib/types/models/affectedStaff/affected-staff-input'
import { StaffRequestProperties } from '../../../lib/types/models/staff-request/properties.staff-request'
import { StaffRequest } from '../../../lib/types/models/staff-request/staff-request'
import { createTimeSchema } from '../../../lib/utils/validations/date.validation'
import { createEnumSchema } from '../../../lib/utils/validations/enum-validation'
import { createNumberSchema } from '../../../lib/utils/validations/int-array-validation'
import { PHONE_NUMBER_SCHEMA } from '../../../lib/utils/validations/phone-number.validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'

export const affectedStaffSchema: ZodType<AffectedStaffInput> = z.object({
  status: createEnumSchema({ enumType: AffectedStaffStatusEnum, fieldName: 'status' }).default(
    AffectedStaffStatusEnum.AFFECTED
  ),
  sentEndOfStayReport: z.boolean().optional(),
  startDate: createStringSchema('date de début'),
  endDate: createStringSchema('date de fin'),
  tripId: createNumberSchema('voyage').optional(),
  associatedRadId: createNumberSchema('associatedRadId', { nullish: true }),
  appointmentTime: createTimeSchema({ inputName: 'heure de convocation' }).nullish(),
  currency: createEnumSchema({ enumType: CurrencyEnum, fieldName: 'currency', nullish: true }),
  baseSalary: createNumberSchema('salaire de base', { nullish: true }),
  exceptionalBonusAmount: createNumberSchema('prime exceptionnelle', { nullish: true }),
  exceptionalBonusReason: createStringSchema('motif exceptionnel', { nullish: true })
})

export const affectStaffViewFormSchema = z.object({})

export const mapStaffRequestToAffectedStaffInput = (
  staffRequest: StaffRequest
): Partial<AffectedStaffInput> => {
  const { startDate, endDate, radId, tripId } = staffRequest
  const centerRad = staffRequest.centers[0]?.rad
  const centerRadId = staffRequest.centers[0]?.radId
  const resolvedRadId = centerRadId ?? radId

  const baseData = {
    // Foreign (UK) staff are paid in GBP, French staff in EUR.
    currency: StaffRequestProperties.isForeignStaff(staffRequest)
      ? CurrencyEnum.GBP
      : CurrencyEnum.EUR,
    ...(startDate && { startDate }),
    ...(endDate && { endDate }),
    ...(tripId && { tripId })
  }
  if (StaffRequestProperties.isForeignStaff(staffRequest)) return baseData

  return {
    ...baseData,
    ...(resolvedRadId && { associatedRadId: resolvedRadId }),
    ...(centerRad?.hours && { appointmentTime: centerRad.hours })
  }
}

export const updateAffectedStaffSchema = (
  t: TFunction<AvailableLanguages, undefined>
): ZodType<AffectedStaffInput> =>
  z.object({
    // null is kept (and not turned into undefined) so that picking "Aucune"
    // reaches the backend and actually unassigns the trip
    tripId: z.preprocess(
      (value) => (value === '' ? null : value),
      createNumberSchema('voyage', { nullish: true })
    ) as z.ZodType<number | null | undefined>,
    sentEndOfStayReport: z.boolean().optional(),
    expenseIncludeHotelBooking: z.boolean().optional(),
    associatedRadId: createNumberSchema('associatedRadId', { nullish: true }),
    appointmentTime: createTimeSchema({ inputName: 'heure de convocation' }).nullish(),
    currency: createEnumSchema({ enumType: CurrencyEnum, fieldName: 'currency', nullish: true }),
    baseSalary: createNumberSchema('salaire de base', { nullish: true }),
    exceptionalBonusAmount: createNumberSchema('prime exceptionnelle', { nullish: true }),
    exceptionalBonusReason: createStringSchema('motif exceptionnel', { nullish: true }),
    participatedInRad: z.boolean(),
    facNumber: PHONE_NUMBER_SCHEMA(t).nullish(),
    rating: createNumberSchema('évaluation', { nullish: true }),
    comment: createStringSchema('commentaire', { nullish: true }),
    travelChoice: createEnumSchema({
      enumType: TravelChoiceEnum,
      fieldName: 'travelChoice',
      nullish: true
    })
  })
